<template>
  <div class="tabla-rooming">
    <input
      v-model="searchQuery"
      type="text"
      class="search-input mb-3 fs-16 fs-m-13"
      :placeholder="$t('rooming.tabla.buscarNombre')" />

    <div class="tabs-container mb-3 fs-16 fs-m-11">
      <button
        :class="['tab-button', { active: activeTab === TABS.SIN_REPARTIR }]"
        @click="activeTab = TABS.SIN_REPARTIR">
        {{ t('rooming.tabla.sinRepartir') }} ({{ usuariosData.sinRepartir.length }}/{{ totalUsuarios }})
      </button>
      <button :class="['tab-button', { active: activeTab === TABS.REPARTIDOS }]" @click="activeTab = TABS.REPARTIDOS">
        {{ t('rooming.tabla.repartidos') }} ({{ usuariosData.repartidos.length }}/{{ totalUsuarios }})
      </button>
    </div>

    <div class="ordenar-container mb-3">
      <div class="ordenar-select fs-14" @click="toggleDropdown">
        <span>{{
          ordenarPor === ORDENAR_TIPOS.GRUPO ? t('rooming.tabla.ordenarPorGrupo') : t('rooming.tabla.ordenarPorNombre')
        }}</span>
        <CustomIcon :icon="arrowDown" size="15px" />
      </div>

      <div v-if="showDropdown" class="dropdown-options">
        <div class="dropdown-option fs-14" @click="selectOption(ORDENAR_TIPOS.GRUPO)">
          {{ t('rooming.tabla.ordenarPorGrupo') }}
        </div>
        <div class="dropdown-option fs-14" @click="selectOption(ORDENAR_TIPOS.NOMBRE)">
          {{ t('rooming.tabla.ordenarPorNombre') }}
        </div>
      </div>
    </div>

    <div class="tabla-container">
      <table class="table table-hover">
        <thead class="table-head fs-13 fw-semibold">
          <tr v-if="ordenarPor === ORDENAR_TIPOS.NOMBRE">
            <th class="sortable" @click="sortTable(SORT_KEYS.NOMBRE)">
              {{ t('rooming.tabla.nombre') }} <i :class="getSortIcon(SORT_KEYS.NOMBRE)" class="fs-12"></i>
            </th>
            <th class="sortable" @click="sortTable(SORT_KEYS.CODIGO)">
              {{ t('rooming.tabla.grupoReserva') }} <i :class="getSortIcon(SORT_KEYS.CODIGO)" class="fs-12"></i>
            </th>
          </tr>
          <tr v-if="ordenarPor === ORDENAR_TIPOS.GRUPO">
            <th class="sortable" @click="sortTable(SORT_KEYS.NOMBRE)">
              {{ t('rooming.tabla.nombre') }} <i :class="getSortIcon(SORT_KEYS.NOMBRE)" class="fs-12"></i>
            </th>
          </tr>
        </thead>

        <tbody>
          <template v-if="ordenarPor === ORDENAR_TIPOS.GRUPO">
            <template v-for="(usuarios, nombreGrupo) in datosParaMostrar.grupos" :key="nombreGrupo">
              <tr class="grupo-header-row">
                <td class="grupo-header-cell fs-14 fw-semibold">{{ nombreGrupo }}</td>
              </tr>
              <tr
                v-for="user in usuarios"
                :key="user.id"
                :class="{
                  clickable: !user.asignado && editMode,
                  'repartido-row': user.asignado,
                  nonclickable: user.asignado || !editMode,
                }"
                @click="seleccionarUsuario(user)">
                <td>{{ user.nombre }} {{ user.apellidos }}</td>
              </tr>
            </template>
          </template>

          <template v-if="ordenarPor === ORDENAR_TIPOS.NOMBRE">
            <tr
              v-for="user in datosParaMostrar.lista"
              :key="user.id"
              :class="{
                clickable: !user.asignado && editMode,
                'repartido-row': user.asignado,
                nonclickable: user.asignado || !editMode,
              }"
              @click="seleccionarUsuario(user)">
              <td>{{ user.nombre }} {{ user.apellidos }}</td>
              <td>{{ getGrupoName(user.codigo) }}</td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, nextTick } from 'vue';
  import { useI18n } from 'vue-i18n';
  import CustomIcon from '@/components/CustomIcon.vue';
  import arrowDown from '@/assets/icons/arrow_down.svg';
  import { ORDENAR_TIPOS, TABS, SORT_KEYS } from '@/constants/roomingConstants';

  const props = defineProps({
    usuarios: {
      type: Array,
      required: true,
    },
    isEditing: {
      type: Boolean,
      required: false,
      default: false,
    },
  });

  const emit = defineEmits(['seleccionar-usuario']);

  const { t } = useI18n();

  const searchQuery = ref('');
  const sortKey = ref('');
  const activeTab = ref(TABS.SIN_REPARTIR);
  const showDropdown = ref(false);
  const ordenarPor = ref(ORDENAR_TIPOS.GRUPO);
  const sortOrders = ref({
    [SORT_KEYS.NOMBRE]: 1,
    [SORT_KEYS.CODIGO]: 1,
  });

  const editMode = computed(() => props.isEditing);
  const totalUsuarios = computed(() => props.usuarios.length);

  const gruposInfo = computed(() => {
    const gruposByCodigo = new Map();
    const grupos = {};
    let i = 1;

    props.usuarios.forEach(usuario => {
      if (usuario.codigo && !gruposByCodigo.has(usuario.codigo)) {
        gruposByCodigo.set(usuario.codigo, `${t('rooming.tabla.grupoReserva')} ${i}`);
        ++i;
      }
    });

    props.usuarios.forEach(usuario => {
      const nombreGrupo = usuario.codigo ? gruposByCodigo.get(usuario.codigo) : t('rooming.tabla.sinGrupo');

      if (!Object.prototype.hasOwnProperty.call(grupos, nombreGrupo)) {
        grupos[nombreGrupo] = [];
      }

      grupos[nombreGrupo].push(usuario);
    });

    return { gruposByCodigo, grupos };
  });

  const usuariosData = computed(() => {
    const sinRepartir = [];
    const repartidos = [];

    props.usuarios.forEach(user => {
      const fullName = `${user.nombre.toLowerCase()} ${user.apellidos?.toLowerCase() || ''}`;
      const matchSearch = fullName.includes(searchQuery.value.toLowerCase());

      if (matchSearch) {
        if (user.asignado) {
          repartidos.push(user);
        } else {
          sinRepartir.push(user);
        }
      }
    });

    return { sinRepartir, repartidos };
  });

  const datosParaMostrar = computed(() => {
    const usuariosActivos =
      activeTab.value === TABS.SIN_REPARTIR ? usuariosData.value.sinRepartir : usuariosData.value.repartidos;

    if (ordenarPor.value === ORDENAR_TIPOS.GRUPO) {
      const grupos = {};
      usuariosActivos.forEach(usuario => {
        const nombreGrupo = getGrupoName(usuario.codigo);
        if (!grupos[nombreGrupo]) {
          grupos[nombreGrupo] = [];
        }
        grupos[nombreGrupo].push(usuario);
      });

      Object.keys(grupos).forEach(nombreGrupo => {
        grupos[nombreGrupo] = ordenarUsuarios(grupos[nombreGrupo]);
      });

      return { grupos, lista: [] };
    } else {
      return {
        grupos: {},
        lista: ordenarUsuarios(usuariosActivos),
      };
    }
  });

  function getGrupoName(codigo) {
    if (!codigo) return 'Sin grupo';
    return gruposInfo.value.gruposByCodigo.get(codigo) || 'Sin grupo';
  }

  function ordenarUsuarios(usuarios) {
    return [...usuarios].sort((a, b) => {
      const order = sortOrders.value[sortKey.value] || 1;
      let aVal, bVal;

      if (sortKey.value === SORT_KEYS.CODIGO) {
        aVal = getGrupoName(a.codigo);
        bVal = getGrupoName(b.codigo);
      } else {
        aVal = `${a.nombre} ${a.apellidos}`.toLowerCase();
        bVal = `${b.nombre} ${b.apellidos}`.toLowerCase();
      }

      if (aVal < bVal) return -1 * order;
      if (aVal > bVal) return 1 * order;
      return 0;
    });
  }

  function getSortIcon(key) {
    if (sortKey.value === key) {
      return sortOrders.value[key] === 1 ? 'fa fa-arrow-up' : 'fa fa-arrow-down';
    }
    return 'fa fa-arrow-up text-transparent';
  }

  function sortTable(key) {
    if (sortKey.value === key) {
      sortOrders.value[key] = sortOrders.value[key] * -1;
    } else {
      sortKey.value = key;
      sortOrders.value[key] = 1;
    }
  }

  function seleccionarUsuario(user) {
    if (!user.asignado && editMode.value) {
      emit('seleccionar-usuario', user);
      nextTick();
    }
  }

  function toggleDropdown() {
    showDropdown.value = !showDropdown.value;
  }

  function selectOption(option) {
    ordenarPor.value = option;
    showDropdown.value = false;
  }
</script>

<style scoped lang="scss">
  .tabla-rooming {
    width: 100%;
  }

  .search-input {
    width: 100%;
    height: 30px;
    text-align: left;
    border-radius: var(--bs-border-radius-xl);
    background: rgba(var(--bs-primary-rgb), 0.02);
    box-shadow: 0 0 3px 0 rgba(var(--bs-primary-rgb), 0.35) inset;
    border: none;
    padding-left: 10px;
    margin-bottom: 10px;
    color: rgba(var(--bs-primary-rgb), 0.6);
  }

  .search-input::placeholder {
    color: rgba(var(--bs-primary-rgb), 0.6);
    opacity: 0.68;
  }

  .tabs-container {
    display: flex;
    border-bottom: 1px solid rgba(var(--bs-primary-rgb), 0.2);
    background: transparent;
    overflow: visible;
    border-radius: 0;
  }

  .tab-button {
    flex: 1;
    padding: 12px 16px;
    border: none;
    background: transparent;
    color: var(--bs-primary);
    cursor: pointer;
    transition: all 0.3s ease;
    border-bottom: 3px solid transparent;
    margin-bottom: -1px;

    @media (max-width: 768px) {
      padding: 10px 12px;
    }

    &:hover:not(.active) {
      border-bottom-color: rgba(var(--bs-primary-rgb), 0.3);
    }

    &.active {
      background: transparent;
      color: var(--bs-primary);
      font-weight: 600;
      border-bottom-color: var(--bs-primary);
    }
  }

  .ordenar-container {
    display: flex;
    justify-content: flex-start;
    position: relative;
  }

  .ordenar-select {
    padding: 10px;
    border: 1px solid rgba(var(--bs-primary-rgb), 0.2);
    border-radius: 20px;
    background: white;
    color: rgba(var(--bs-primary-rgb), 0.8);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-width: 200px;
    width: 100%;
    max-width: 300px;

    @media (max-width: 768px) {
      min-width: unset;
      width: 100%;
      padding: 8px 12px;
    }

    &:focus {
      outline: none;
      border-color: var(--bs-primary);
    }

    &:hover {
      border-color: rgba(var(--bs-primary-rgb), 0.4);
    }
  }

  .dropdown-options {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    border: 1px solid rgba(var(--bs-primary-rgb), 0.2);
    border-radius: 12px;
    z-index: 10;
    background-color: white;
    width: 100%;

    @media (max-width: 768px) {
      left: 0;
      right: 0;
      width: 100%;
    }
  }

  .dropdown-option {
    padding: 12px 16px;
    cursor: pointer;
    color: rgba(var(--bs-primary-rgb), 0.8);

    @media (max-width: 768px) {
      padding: 10px 12px;
    }

    &:hover {
      background-color: rgba(var(--bs-primary-rgb), 0.05);
    }

    &:first-child {
      border-radius: 12px 12px 0 0;
    }

    &:last-child {
      border-radius: 0 0 12px 12px;
    }
  }

  .tabla-container {
    border-radius: 12px;
    border: 1px solid rgba(var(--bs-primary-rgb), 0.2);
    overflow: hidden;
    background-color: white;
  }

  .table {
    margin-bottom: 0;
    border: none;
    table-layout: fixed;
    width: 100%;

    &:has(thead tr th:nth-child(2)) {
      th,
      td {
        width: 50%;
      }
    }

    &:has(thead tr th:only-child) {
      th,
      td {
        width: 100%;
      }
    }

    th,
    td {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      padding: 8px 12px;
      border-color: rgba(var(--bs-primary-rgb), 0.1) !important;
      vertical-align: middle;
    }

    th {
      height: 35px;
      line-height: 19px;
    }

    td {
      height: 40px;
      line-height: 24px;
    }

    .grupo-header-cell {
      width: 100% !important;
      height: 35px;
      line-height: 19px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .table-head {
    background-color: #f9f7f8 !important;
    opacity: 1 !important;
  }

  .grupo-header-row {
    .grupo-header-cell {
      border-bottom: 1px solid rgba(var(--bs-primary-rgb), 0.2);
      background-color: #f9f7f8 !important;
    }
  }

  .sortable {
    cursor: pointer;
    color: rgba(var(--bs-primary-rgb), 0.6);
    opacity: 0.68;
  }

  .clickable {
    cursor: pointer;
    transition: background-color 0.2s ease;
  }

  .nonclickable {
    cursor: not-allowed;
  }

  .repartido-row {
    background-color: transparent !important;
    opacity: 0.6;
  }

  td {
    color: var(--bs-primary) !important;
  }

  th {
    color: rgba(var(--bs-primary-rgb), 0.6);
    opacity: 0.68;
    font-weight: 600;
  }

  @media (max-width: 768px) {
    .d-none.d-md-table-cell {
      display: none !important;
    }
  }
</style>
